import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Anchor, Compass, Ship, Shield, History, User, LogOut, BarChart3, Clock, Play, AlertCircle } from "lucide-react";
import { toast } from "sonner";

const FUNCTIONS = [
  {
    num: 1,
    title: "Function 1: Navigation",
    subtitle: "COLREG, ECDIS, Compass, Charts, GMDSS, IAMSAR, Meteorology",
    icon: Compass,
    color: "bg-teal-50 text-teal-700 border-teal-200",
    iconColor: "text-teal-600",
  },
  {
    num: 2,
    title: "Function 2: Cargo Handling",
    subtitle: "IMDG Code, Cargo Securing, Dangerous Goods, Ventilation, Grain Loading",
    icon: Ship,
    color: "bg-amber-50 text-amber-700 border-amber-200",
    iconColor: "text-amber-600",
  },
  {
    num: 3,
    title: "Function 3: Ship Operations & Safety",
    subtitle: "SOLAS, MARPOL, LSA, FFA, ISM Code, Stability, Medical, Pollution Prevention",
    icon: Shield,
    color: "bg-blue-50 text-blue-700 border-blue-200",
    iconColor: "text-blue-600",
  },
];

export default function DashboardPage() {
  const { user, logout, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [starting, setStarting] = useState(null);

  useEffect(() => { refreshUser(); }, [refreshUser]);

  const planLabel = {
    free: "Free Trial",
    weekly: "Weekly Pass",
    monthly: "Monthly Plan",
    all_access: "All Access",
  };

  const sessionsRemaining = Math.max(0, 5 - (user?.sessions_today || 0));
  const isFreeExhausted = user?.plan === "free" && (user?.free_sessions_used || 0) >= (user?.max_free_sessions || 2);

  const handleStart = async (functionNum) => {
    if (isFreeExhausted) {
      toast.error("Upgrade to continue practicing.", { description: "Your free sessions are used up." });
      navigate("/account");
      return;
    }
    if (sessionsRemaining <= 0) {
      toast.error("Daily limit reached. Come back tomorrow!");
      return;
    }
    if (user?.active_session_id) {
      navigate(`/session/${user.active_session_id}`);
      return;
    }
    setStarting(functionNum);
    try {
      const { data } = await api.post("/sessions/start", { function_number: functionNum });
      navigate(`/session/${data.session_id}`, { state: { initialMessage: data.ai_message, functionName: data.function_name } });
    } catch (err) {
      const msg = err.response?.data?.detail || "Failed to start session";
      if (err.response?.status === 402) {
        navigate("/account");
      }
      toast.error(msg);
    } finally {
      setStarting(null);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="cop-glass border-b sticky top-0 z-40" data-testid="dashboard-header">
        <div className="container mx-auto px-4 md:px-6 max-w-5xl flex items-center justify-between h-14">
          <Link to="/dashboard" className="flex items-center gap-2">
            <Anchor className="h-5 w-5 text-primary" />
            <span className="font-bold text-[#0a1628] hidden sm:inline" style={{ fontFamily: 'Manrope' }}>CalmOceanPrep</span>
          </Link>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" asChild data-testid="nav-history-btn">
              <Link to="/history"><History className="h-4 w-4 mr-1" /> History</Link>
            </Button>
            <Button variant="ghost" size="sm" asChild data-testid="nav-account-btn">
              <Link to="/account"><User className="h-4 w-4" /></Link>
            </Button>
            <Button variant="ghost" size="sm" onClick={() => { logout(); navigate("/"); }} data-testid="nav-logout-btn">
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 md:px-6 max-w-5xl py-6 md:py-10" data-testid="dashboard-main">
        {/* Welcome */}
        <div className="mb-8">
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight mb-1" style={{ fontFamily: 'Manrope' }} data-testid="dashboard-welcome">
            Welcome back
          </h1>
          <p className="text-muted-foreground text-sm">{user?.email}</p>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
          <Card className="border">
            <CardContent className="p-4">
              <p className="text-xs text-muted-foreground mb-1">Plan</p>
              <Badge variant="secondary" className="text-xs" data-testid="plan-badge">{planLabel[user?.plan] || "Free Trial"}</Badge>
            </CardContent>
          </Card>
          <Card className="border">
            <CardContent className="p-4">
              <p className="text-xs text-muted-foreground mb-1">Sessions Today</p>
              <p className="text-lg font-semibold" data-testid="sessions-today">{user?.sessions_today || 0}/5</p>
            </CardContent>
          </Card>
          <Card className="border">
            <CardContent className="p-4">
              <p className="text-xs text-muted-foreground mb-1">Total Sessions</p>
              <p className="text-lg font-semibold" data-testid="total-sessions">{user?.total_sessions || 0}</p>
            </CardContent>
          </Card>
          <Card className="border">
            <CardContent className="p-4">
              <p className="text-xs text-muted-foreground mb-1">Avg Score</p>
              <p className="text-lg font-semibold" data-testid="avg-score">{user?.avg_score || 0}/10</p>
            </CardContent>
          </Card>
        </div>

        {/* Active Session Warning */}
        {user?.active_session_id && (
          <Card className="border-amber-300 bg-amber-50 mb-6" data-testid="active-session-warning">
            <CardContent className="p-4 flex items-center gap-3">
              <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-medium text-amber-800">You have a session in progress</p>
                <p className="text-xs text-amber-600">Complete or end it before starting a new one.</p>
              </div>
              <Button size="sm" variant="outline" className="border-amber-300" asChild>
                <Link to={`/session/${user.active_session_id}`}>Continue</Link>
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Free Trial Warning */}
        {isFreeExhausted && (
          <Card className="border-primary/30 bg-teal-50 mb-6" data-testid="paywall-card">
            <CardContent className="p-4">
              <p className="text-sm font-medium text-teal-800 mb-1">Free sessions used up</p>
              <p className="text-xs text-teal-600 mb-3">Upgrade to continue preparing for your MMD orals.</p>
              <Button size="sm" asChild><Link to="/account">View Plans</Link></Button>
            </CardContent>
          </Card>
        )}

        {/* Function Cards */}
        <h2 className="text-lg font-semibold mb-4" style={{ fontFamily: 'Manrope' }}>Practice Functions</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          {FUNCTIONS.map((fn) => (
            <Card key={fn.num} className="border hover:shadow-md transition-shadow cursor-pointer group" data-testid={`function-card-${fn.num}`}>
              <CardContent className="p-6">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-4 ${fn.color}`}>
                  <fn.icon className={`h-5 w-5 ${fn.iconColor}`} />
                </div>
                <h3 className="font-semibold mb-1">{fn.title}</h3>
                <p className="text-xs text-muted-foreground leading-relaxed mb-4">{fn.subtitle}</p>
                <Button
                  className="w-full"
                  onClick={() => handleStart(fn.num)}
                  disabled={starting === fn.num || (user?.active_session_id && !starting)}
                  data-testid={`start-practice-${fn.num}`}
                >
                  {starting === fn.num ? (
                    <span className="flex items-center gap-2"><Clock className="h-4 w-4 animate-spin" /> Starting...</span>
                  ) : (
                    <span className="flex items-center gap-2"><Play className="h-4 w-4" /> Start Practice</span>
                  )}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Quick Links */}
        <div className="flex flex-wrap gap-3">
          <Button variant="outline" size="sm" asChild>
            <Link to="/history"><History className="h-4 w-4 mr-1" /> Session History</Link>
          </Button>
          <Button variant="outline" size="sm" asChild>
            <Link to="/account"><User className="h-4 w-4 mr-1" /> Account & Billing</Link>
          </Button>
        </div>
      </main>
    </div>
  );
}
